import React, { useState, useRef } from 'react';
import { Modal, Button, Checkbox, message } from 'antd';
import { Trash2, AlertTriangle, AlertCircle, XCircle } from 'lucide-react';
import { deleteStudiesFromClinical } from '../../view/studies/StudiesService';

/* ── Figma-extracted design tokens ──────────────────────────────── */
const COLORS = {
  red600: '#dc2626',
  red700: '#b91c1c',
  red500: '#ef4444',
  red300: '#fca5a5',
  red50: '#fef2f2',
  red900: '#7f1d1d',
  red800: '#991b1b',
  amber50: '#fffbeb',
  amber100: '#fef3c7',
  amber200: '#fde68a',
  amber600: '#d97706',
  amber700: '#b45309',
  amber800: '#92400e',
  amber900: '#78350f',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
};

const overlayStyle = {
  backgroundColor: 'rgba(0,0,0,0.5)',
};

const modalBodyStyle = {
  padding: 24,
};

const titleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
};

const titleTextStyle = {
  fontSize: 18,
  fontWeight: 600,
  lineHeight: 1,
  color: COLORS.gray900,
  margin: 0,
};

const descriptionStyle = {
  fontSize: 14,
  color: COLORS.gray600,
  margin: 0,
};

const sectionGap = { marginTop: 24 };

/* Warning banner */
const warningBannerStyle = {
  background: COLORS.red50,
  borderLeft: `4px solid ${COLORS.red600}`,
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  gap: 12,
};

/* Summary card */
const summaryCardStyle = {
  background: COLORS.gray50,
  borderRadius: 8,
  padding: 16,
  border: `1px solid ${COLORS.gray200}`,
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  textAlign: 'center',
};

const bigNumberStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: COLORS.gray900,
  lineHeight: 1.2,
};

const smallLabelStyle = {
  fontSize: 12,
  color: COLORS.gray600,
  marginTop: 4,
};

/* Amber warning */
const amberWarningStyle = {
  background: COLORS.amber50,
  border: `1px solid ${COLORS.amber200}`,
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  gap: 12,
};

/* Studies list */
const listContainerStyle = {
  background: COLORS.gray50,
  border: `1px solid ${COLORS.gray200}`,
  borderRadius: 8,
  padding: 12,
  maxHeight: 192,
  overflowY: 'auto',
};

const listItemStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: 4,
  cursor: 'default',
};

const listItemNameStyle = {
  fontSize: 14,
  fontWeight: 500,
  color: COLORS.gray900,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const listItemDetailStyle = {
  fontSize: 12,
  color: COLORS.gray500,
};

const completedBadgeStyle = {
  fontSize: 12,
  background: COLORS.amber100,
  color: COLORS.amber700,
  padding: '4px 8px',
  borderRadius: 4,
};

/* Confirmation */
const confirmBoxStyle = {
  background: COLORS.gray50,
  borderRadius: 8,
  padding: 16,
  border: `2px solid ${COLORS.gray200}`,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};

/* Progress */
const progressTrackStyle = {
  width: '100%',
  background: COLORS.gray200,
  borderRadius: 9999,
  height: 8,
  overflow: 'hidden',
};

const progressFillBase = {
  background: COLORS.red600,
  height: '100%',
  transition: 'width 300ms',
  borderRadius: 9999,
};

/* Footer */
const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  paddingTop: 16,
  borderTop: `1px solid ${COLORS.gray200}`,
  marginTop: 24,
};

const sectionLabelStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: COLORS.gray900,
  marginBottom: 12,
};

export function BulkDeleteDialog({ studies, open, onOpenChange, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);

  // ── draggable modal support ──────────────────────────────────────────────
  const dragState = useRef({ dragging: false, startX: 0, startY: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.target.closest('button')) return;
    dragState.current = { dragging: true, startX: e.clientX - pos.x, startY: e.clientY - pos.y };
    const onMouseMove = (ev) => {
      if (!dragState.current.dragging) return;
      setPos({ x: ev.clientX - dragState.current.startX, y: ev.clientY - dragState.current.startY });
    };
    const onMouseUp = () => {
      dragState.current.dragging = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const totalImages = (studies || []).reduce((sum, s) => sum + (parseInt(s.imagesCount, 10) || 0), 0);
  const hasCompletedStudies = (studies || []).some(
    (s) => (s.status || '').toLowerCase() === 'completed'
  );

  const handleDelete = async () => {
    if (!confirmDelete) {
      message.error('Please confirm deletion by checking the confirmation box');
      return;
    }

    const studyList = studies || [];
    if (studyList.length === 0) return;

    setDeleting(true);
    setDeleteProgress(0);

    try {
      // Tenant-aware, index-cleaning delete — the SAME backend the single-study
      // delete uses. The old per-study raw `axios.delete('/orthanc/studies/..')`
      // hit the DEFAULT Orthanc only, so studies in any other tenant 404'd and
      // silently failed (nothing deleted); it also left the ES clinical index,
      // assignments, and reports orphaned. `/api/studies/delete_clinical`
      // resolves the right tenant Orthanc and cleans everything up.
      const study_ids = studyList.map((s) => s.id).filter(Boolean);
      const study_instance_uids = studyList.map((s) => s.studyInstanceUID).filter(Boolean);

      setDeleteProgress(50);
      const response = await deleteStudiesFromClinical({ study_ids, study_instance_uids });
      setDeleteProgress(100);

      const deleted = response?.data?.data?.deleted || 0;
      const failed = response?.data?.data?.failed || 0;
      if (deleted > 0 && failed === 0) {
        message.success(`${deleted} ${deleted === 1 ? 'study' : 'studies'} permanently removed from the system`);
      } else if (deleted > 0) {
        message.warning(`Deleted ${deleted} of ${studyList.length} studies, ${failed} failed`);
      } else {
        message.error('No studies were deleted');
      }

      if (onDelete) {
        onDelete(studies);
      }
      setTimeout(() => {
        onOpenChange(false);
        setConfirmDelete(false);
        setDeleteProgress(0);
      }, 1000);
    } catch (error) {
      console.error('Bulk delete error:', error);
      message.error(error.response?.data?.message || 'Failed to delete studies');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      onOpenChange(false);
      setConfirmDelete(false);
      setDeleteProgress(0);
    }
  };

  const studyCount = (studies || []).length;

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      footer={null}
      width={672}
      centered
      destroyOnClose
      maskStyle={overlayStyle}
      bodyStyle={modalBodyStyle}
      closable={!deleting}
      modalRender={(modal) => (
        <div style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}>
          {modal}
        </div>
      )}
    >
      {/* Header — drag handle */}
      <div onMouseDown={onMouseDown} style={{ cursor: 'move', userSelect: 'none' }}>
        <div style={titleRowStyle}>
          <Trash2 size={20} color={COLORS.red600} />
          <h3 style={titleTextStyle}>Delete Multiple Studies</h3>
        </div>
        <p style={descriptionStyle}>
          You are about to permanently delete {studyCount} {studyCount === 1 ? 'study' : 'studies'}
        </p>
      </div>

      {/* Warning Banner */}
      <div style={sectionGap}>
        <div style={warningBannerStyle}>
          <AlertTriangle size={20} color={COLORS.red600} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.red900, marginBottom: 4 }}>
              Warning: This action cannot be undone
            </div>
            <div style={{ fontSize: 14, color: COLORS.red800 }}>
              All DICOM images, associated reports, and metadata will be permanently removed from the system.
            </div>
          </div>
        </div>
      </div>

      {/* Deletion Summary */}
      <div style={sectionGap}>
        <div style={summaryCardStyle}>
          <div style={summaryGridStyle}>
            <div>
              <div style={bigNumberStyle}>{studyCount}</div>
              <div style={smallLabelStyle}>Studies to Delete</div>
            </div>
            <div>
              <div style={bigNumberStyle}>{totalImages}</div>
              <div style={smallLabelStyle}>Total Images</div>
            </div>
          </div>
        </div>
      </div>

      {/* Completed Studies Warning */}
      {hasCompletedStudies && (
        <div style={sectionGap}>
          <div style={amberWarningStyle}>
            <AlertCircle size={20} color={COLORS.amber600} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.amber900, marginBottom: 4 }}>
                Completed Studies Included
              </div>
              <div style={{ fontSize: 14, color: COLORS.amber800 }}>
                Some selected studies have completed reports. Deleting these will remove finalized medical records.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Studies to be Deleted */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Studies to be Deleted</div>
        <div style={listContainerStyle}>
          {(studies || []).map((study) => (
            <div key={study.id} style={listItemStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={listItemNameStyle}>{study.patientName}</div>
                <div style={listItemDetailStyle}>
                  {study.patientID} &bull; {study.modality} &bull; {study.imagesCount || 0} images
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 8 }}>
                {(study.status || '').toLowerCase() === 'completed' && (
                  <span style={completedBadgeStyle}>Completed</span>
                )}
                <XCircle size={16} color={COLORS.red500} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div style={sectionGap}>
        <div style={confirmBoxStyle}>
          <Checkbox
            checked={confirmDelete}
            onChange={(e) => setConfirmDelete(e.target.checked)}
            style={{ marginTop: 4 }}
          />
          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setConfirmDelete(!confirmDelete)}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gray900 }}>
              I understand that this action is permanent and cannot be undone.
            </div>
            <div style={{ fontSize: 14, color: COLORS.gray600, marginTop: 4 }}>
              I confirm that I want to permanently delete {studyCount} {studyCount === 1 ? 'study' : 'studies'} with all associated data.
            </div>
          </div>
        </div>
      </div>

      {/* Delete Progress */}
      {deleting && (
        <div style={sectionGap}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
            <span style={{ color: COLORS.gray900 }}>Deleting studies...</span>
            <span style={{ color: COLORS.red600, fontWeight: 600 }}>{deleteProgress}%</span>
          </div>
          <div style={progressTrackStyle}>
            <div style={{ ...progressFillBase, width: `${deleteProgress}%` }} />
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={footerStyle}>
        <Button
          onClick={handleClose}
          disabled={deleting}
          style={{
            border: `1px solid ${COLORS.gray300}`,
            background: COLORS.white,
            color: COLORS.gray700,
            height: 36,
            padding: '0 16px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          danger
          onClick={handleDelete}
          disabled={!confirmDelete || deleting}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: !confirmDelete || deleting ? undefined : COLORS.red600,
            borderColor: !confirmDelete || deleting ? undefined : COLORS.red600,
            color: COLORS.white,
            height: 36,
            padding: '0 16px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            opacity: !confirmDelete ? 0.5 : 1,
            cursor: !confirmDelete ? 'not-allowed' : 'pointer',
          }}
        >
          {deleting ? (
            <>
              <Trash2 size={16} />
              Deleting...
            </>
          ) : (
            <>
              <Trash2 size={16} />
              Delete {studyCount} {studyCount === 1 ? 'Study' : 'Studies'}
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
}
